import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Subscribes to the SSE leaderboard stream for a tournament. Every event
 * triggers an invalidation of the corresponding leaderboard query in React
 * Query, so the page refetches without us having to embed the full
 * snapshot in the SSE payload.
 *
 * Reconnect strategy: exponential backoff 1s → 2s → 4s → 8s → 30s cap.
 * Resets to 1s on successful event.
 *
 * Disabled until a valid tournamentId is provided.
 */
export function useLeaderboardStream(tournamentId: number | null | undefined) {
  const qc = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const timerRef = useRef<number | null>(null);
  const backoffRef = useRef<number>(1000);
  // Latest tournamentId in a ref so the connect closure doesn't go stale.
  const idRef = useRef<number | null>(null);

  useEffect(() => {
    idRef.current = tournamentId ?? null;
    if (!tournamentId) return;

    let cancelled = false;
    const MAX_BACKOFF = 30_000;
    const BASE_BACKOFF = 1_000;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(delay * 2, MAX_BACKOFF);
      timerRef.current = window.setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      if (idRef.current !== tournamentId) return;

      // Close any existing connection before opening a new one.
      esRef.current?.close();
      const url = `/api/tournaments/${tournamentId}/leaderboard/stream`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      const onAnyMessage = () => {
        // First real message means the connection works — reset the backoff.
        backoffRef.current = BASE_BACKOFF;
        qc.invalidateQueries({ queryKey: ['leaderboard', tournamentId] });
      };

      es.addEventListener('hello', () => {
        backoffRef.current = BASE_BACKOFF;
      });
      es.addEventListener('leaderboard-update', onAnyMessage);
      // Some browsers fire the default 'message' event for unnamed data.
      es.onmessage = onAnyMessage;

      es.onerror = () => {
        // EventSource will retry on its own; we close + manage backoff
        // ourselves so we can implement the requested 1→2→4→8→30 curve.
        es.close();
        if (cancelled) return;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      esRef.current?.close();
      esRef.current = null;
    };
  }, [tournamentId, qc]);
}
